import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../components/UIToolkit/Animations/JumpingDots";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { type StatusChecklistItem, SystemStatusChecklist } from "../../../../../components/UIToolkit/molecules/SystemStatusChecklist";
import SystemDialog, { type DialogCheckboxDescriptor, type DialogCheckedState } from "../../../../../components/UIToolkit/Popups/Dialog";
import { FONT_SIZE, INK } from "../../../../../theme/tokens";
import { agencySourceLabel } from "../../../../../utils/agencyIntegrations";
import type { AgencyDeactivationImpact, AgencySource } from "../../../../../utils/models";
import { useAgencyDeactivationImpact } from "../hooks";
import { ACKNOWLEDGE_CHECKBOX_ID, resolveDeactivationChoices, UNPOST_CHECKBOX_ID } from "./agencyDeactivationChoices";

type Props = {
	open: boolean;
	source: AgencySource;
	onClose: () => void;
	/** Fires with the un-post choice and the preflight the operator actually saw (for analytics). */
	onConfirm: (unpostOpenShifts: boolean, impact: AgencyDeactivationImpact | undefined) => void;
	confirmLoading?: boolean;
};

/**
 * "You are about to stop talking to this agency — here is what is currently in flight."
 *
 * The four rows are the whole point of the dialog: deactivation is cheap to do and expensive to do
 * blind. Row 3 (responses awaiting a decision) is the 2026-09-17 incident stated in the UI — a
 * pending agency response is a clinician the department is about to lose the ability to answer.
 *
 * **Deactivation is never blocked.** If the preflight fails the counts are replaced by a warning and
 * confirm stays ENABLED — an impact-endpoint outage must not become a configuration outage, the same
 * rule the Nursa facility picker follows for its vendor list.
 *
 * What the failure must NOT do is quietly take the un-post decision away. The counts are display only,
 * but `unpostOpenShifts` is a real instruction: the server acts on `body.unpostOpenShifts` and picks
 * the postings itself, so a dialog that omits the checkbox sends `false` and leaves every open posting
 * live at the vendor. The checkbox is therefore offered without a count when the preflight fails — see
 * `unpostOffer` below.
 */
export const AgencyDeactivationDialog: FC<Props> = ({ open, source, onClose, onConfirm, confirmLoading }) => {
	const { t } = useTranslation();
	const agency = agencySourceLabel(source);
	const { data: impact, isLoading, isError } = useAgencyDeactivationImpact(source, open);

	const items = useMemo<StatusChecklistItem[]>(() => {
		if (!impact) return [];
		return [
			{
				id: "open-shifts",
				label: t("department.config.integrations.agency.impact.openShifts.label", { agency }),
				description: t("department.config.integrations.agency.impact.openShifts.description", { agency }),
				// Nothing advertised is genuinely settled; anything advertised is live traffic about to stop.
				state: impact.openShiftsPosted > 0 ? "active" : "done",
				statusLabel: String(impact.openShiftsPosted),
			},
			{
				id: "scheduled-nurses",
				label: t("department.config.integrations.agency.impact.scheduledNurses.label", { agency }),
				description: t("department.config.integrations.agency.impact.scheduledNurses.description", { agency }),
				// "pending" and not "blocked": these bookings are LEFT ALONE by the switch, which is exactly
				// the thing the required acknowledgement below is about.
				state: impact.scheduledAgencyNurses > 0 ? "pending" : "done",
				statusLabel: String(impact.scheduledAgencyNurses),
			},
			{
				id: "pending-responses",
				label: t("department.config.integrations.agency.impact.pendingResponses.label", { agency }),
				description: t("department.config.integrations.agency.impact.pendingResponses.description", { agency }),
				// The only row that can say "cannot". A response left pending here is unanswerable afterwards.
				state: impact.pendingResponses > 0 ? "blocked" : "done",
				statusLabel: String(impact.pendingResponses),
			},
			{
				id: "inbound-events",
				label: t("department.config.integrations.agency.impact.inboundEvents.label", {
					agency,
					// Never hardcoded: the server owns the window and reports it alongside the count.
					count: impact.inboundEventLookbackDays,
				}),
				description: t("department.config.integrations.agency.impact.inboundEvents.description", { agency }),
				// Backward-looking on purpose: it answers "is this integration alive?", not "what changes?".
				state: impact.inboundEventsRecent > 0 ? "active" : "pending",
				statusLabel: String(impact.inboundEventsRecent),
			},
		];
	}, [impact, agency, t]);

	const checkboxes = useMemo<DialogCheckboxDescriptor[]>(() => {
		const descriptors: DialogCheckboxDescriptor[] = [];
		/**
		 * Whether to offer the un-post choice at all — and the answer is NOT "only when we counted some".
		 *
		 * Three cases, and the third is the one that matters:
		 * - counted, above zero → offer it, labelled with the count;
		 * - counted, zero → omit it. A decision about nothing is noise.
		 * - **preflight FAILED → offer it, unlabelled.** Not knowing the count is not the same as knowing
		 *   it is zero. Omitting it here sends `unpostOpenShifts: false`, so the integration goes off with
		 *   every open posting still live at the vendor — clinicians can still pick up shifts this
		 *   department will never hear about, which is the exact state this whole feature exists to
		 *   prevent. The server reads `body.unpostOpenShifts`; it does not re-decide this for us.
		 *
		 * `defaultValue: true` throughout, because leaving seats advertised by an integration that no
		 * longer answers is the outcome nobody wants.
		 */
		const unpostOffer = impact ? (impact.openShiftsPosted > 0 ? "counted" : "none") : isError ? "unknown" : "none";
		if (unpostOffer !== "none") {
			descriptors.push({
				id: UNPOST_CHECKBOX_ID,
				label:
					unpostOffer === "counted"
						? t("department.config.integrations.agency.unpost.label", { agency, count: impact?.openShiftsPosted ?? 0 })
						: t("department.config.integrations.agency.unpost.labelUnknown", { agency }),
				helperText: t("department.config.integrations.agency.unpost.helper", { agency }),
				required: false,
				defaultValue: true,
			});
		}
		descriptors.push({
			id: ACKNOWLEDGE_CHECKBOX_ID,
			label: t("department.config.integrations.agency.acknowledge.label", { agency }),
			helperText: t("department.config.integrations.agency.acknowledge.helper", { agency }),
			required: true,
			defaultValue: false,
		});
		return descriptors;
	}, [impact, isError, agency, t]);

	const handleConfirm = (checkedState?: DialogCheckedState[]) => {
		// Resolved by `agencyDeactivationChoices`, which matches on id (never on the translated,
		// count-interpolated label) and THROWS on an id it does not know. That throw is the point: the
		// previous inline `find(...) ?? false` turned a drifted id into a silent "no un-post".
		const { unpost } = resolveDeactivationChoices(checkedState);
		onConfirm(unpost, impact);
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("department.config.integrations.agency.deactivate.title", { agency })}
			confirm={t("department.config.integrations.agency.deactivate.confirm", { agency })}
			confirmButtonVariant="delete"
			confirmLoading={confirmLoading}
			onConfirm={handleConfirm}
			checkboxes={checkboxes}
			maxWidth="sm"
			fullWidth
			dataTestId="agency-deactivation-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box component="p" sx={{ m: 0, fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.5, color: INK.MUTED }}>
						{t("department.config.integrations.agency.deactivate.lead", { agency })}
					</Box>
					{isLoading && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }} data-testid="agency-deactivation-impact-loading">
							<JumpingDots />
							<Box component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
								{t("department.config.integrations.agency.impact.loading", { agency })}
							</Box>
						</Box>
					)}
					{/* `&& !impact`: a REFETCH that fails while a previous answer is still in the cache leaves
					    `isError` and `data` both truthy, and showing "we couldn't check" directly above four
					    live counts is incoherent — the counts are the better information, so they win. */}
					{isError && !impact && (
						<InlineHint tone="warning" dataTestId="agency-deactivation-impact-error">
							{t("department.config.integrations.agency.impact.failed", { agency })}
						</InlineHint>
					)}
					{impact && (
						<SystemStatusChecklist
							items={items}
							title={t("department.config.integrations.agency.impact.title", { agency })}
							dataTestId="agency-deactivation-impact"
						/>
					)}
				</Box>
			}
		/>
	);
};
